<template>
  <div>
    <page-header :title="title" :container-class="containerClass" />
    <div class="page-section">
      <div :class="containerClass">
        <div class="card mb-0">
          <div class="card-header">
            <div class="row align-items-center" style="white-space: nowrap">
              <div class="col-lg-auto">
                <form class="search-form search-form--light d-lg-inline-flex mb-8pt mb-lg-0" @submit.prevent="">
                  <b-form-input class="w-lg-auto" placeholder="Search names, quotes or schools" v-model="searchTerm" @input="onSearch" />
                  <b-btn variant="flush" type="submit">
                    <md-icon v-text="'search'" />
                  </b-btn>
                </form>
              </div>
              <div class="col-lg-auto">
                <b-form-select v-model="visibility" :options="visibilityOptions" class="w-auto" @change="fetchTestimonials()" />
              </div>

              <div class="col-lg d-flex flex-wrap justify-content-end">
                <b-btn class="ml-2" :to="{ name: 'sa-add-tu-testimonial' }" exact variant="primary" v-text="'Add Testimonial'" />
              </div>
            </div>
          </div>

          <b-table
            :fields="tableColumns"
            :items="testimonials"
            :busy="isLoading"
            head-variant="light"
            hover
            responsive
            no-local-sorting
          >
            <template #cell(name)="data">
              <strong>{{ data.item.name }}</strong>
              <div v-if="data.item.role" class="text-muted small">{{ data.item.role }}</div>
            </template>

            <template #cell(quote)="data">
              <span class="tt-quote">{{ data.item.quote }}</span>
            </template>

            <template #cell(university)="data">
              <span v-if="data.item.university">{{ data.item.university.name }}</span>
              <span v-else-if="data.item.school">{{ data.item.school }}</span>
              <span v-else class="text-muted">None</span>
            </template>

            <template #cell(is_visible)="data">
              <b-badge
                :class="`border border-${data.item.is_visible ? 'success' : 'secondary'} text-${data.item.is_visible ? 'success' : 'secondary'}`"
                variant="none"
              >
                {{ data.item.is_visible ? 'Visible' : 'Hidden' }}
              </b-badge>
            </template>

            <template #head(actions)="">
              <span></span>
            </template>
            <template #cell(actions)="data">
              <router-link :to="{ name: 'sa-edit-tu-testimonial', params: { id: data.item.id } }" class="svg-icon mr-2" v-b-popover.hover.right :title="'Edit'">
                <i class="material-icons">edit</i>
              </router-link>
              <a href="#" class="mr-2" @click.prevent="toggleVisible(data.item)" v-b-popover.hover.right :title="data.item.is_visible ? 'Hide' : 'Show on TransitionU'">
                <md-icon :class="data.item.is_visible ? 'text-secondary' : 'text-success'">
                  {{ data.item.is_visible ? 'visibility_off' : 'visibility' }}
                </md-icon>
              </a>
              <a href="#" @click.prevent="removeTestimonial(data.item)" v-b-popover.hover.right :title="'Delete'">
                <i class="material-icons text-danger">delete</i>
              </a>
            </template>
          </b-table>

          <div class="card-footer">
            <pagination
              v-model="currentPage"
              :total-rows="totalTestimonials"
              :per-page="perPage"
              @change="onPageChange"
              aria-controls="tu-testimonials-table"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PageHeader from '@/components/Ui/PageHeader.vue';
import { debounce, get } from 'lodash';
import { mapActions } from 'vuex';

import Page from '@/components/Page.vue';
import Pagination from '@/components/Ui/Pagination.vue';
import MdIcon from '@/components/Ui/MdIcon.vue';
import { DEFAULT_PAGE_SIZE } from '@/common/constants';

export default {
  components: { MdIcon, PageHeader, Pagination },
  extends: Page,

  data() {
    return {
      title: 'Manage Testimonials',
      isLoading: false,
      perPage: DEFAULT_PAGE_SIZE,
      currentPage: 1,
      totalTestimonials: 0,
      searchTerm: '',
      visibility: '',
      testimonials: [],
      visibilityOptions: [
        { value: '', text: 'All' },
        { value: 'true', text: 'Visible' },
        { value: 'false', text: 'Hidden' },
      ],
    };
  },

  computed: {
    tableColumns() {
      return [
        { key: 'name', label: 'Name' },
        { key: 'quote', label: 'Quote' },
        { key: 'university', label: 'School' },
        { key: 'order', label: 'Order' },
        { key: 'is_visible', label: 'Status' },
        { key: 'actions', tdClass: 'text-right', thStyle: { minWidth: '120px' } },
      ];
    },
  },

  methods: {
    ...mapActions('tuTestimonials', ['getAllTuTestimonials', 'updateTuTestimonial', 'deleteTuTestimonial']),

    async fetchTestimonials(pageNum = 1) {
      this.isLoading = true;
      try {
        const response = await this.getAllTuTestimonials({
          limit: this.perPage,
          offset: (pageNum - 1) * this.perPage,
          ...(this.searchTerm && { search: this.searchTerm }),
          ...(this.visibility && { is_visible: this.visibility }),
        });
        this.testimonials = get(response, 'data.results', []);
        this.currentPage = pageNum;
        this.totalTestimonials = get(response, 'data.count', 0);
      } catch (_err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isLoading = false;
    },

    onPageChange(pageNum) {
      this.fetchTestimonials(pageNum);
    },

    onSearch() {
      this.debouncedSearch(this);
    },

    debouncedSearch: debounce(vm => {
      vm.fetchTestimonials();
    }, 500),

    async toggleVisible(item) {
      try {
        await this.updateTuTestimonial({ id: item.id, data: { is_visible: !item.is_visible } });
        this.fetchTestimonials(this.currentPage);
        this.makeToast({ variant: 'success', msg: item.is_visible ? 'Hidden from TransitionU.' : 'Now shown on TransitionU.' });
      } catch (_err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },

    async removeTestimonial(item) {
      try {
        const isConfirmed = await this.$bvModal.msgBoxConfirm(`Delete the testimonial from "${item.name}"?`, {
          title: 'Are you sure?', size: 'md', buttonSize: 'sm', okVariant: 'danger', okTitle: 'Yes', cancelTitle: 'No', footerClass: 'p-2', centered: true,
        });
        if (isConfirmed) {
          await this.deleteTuTestimonial(item.id);
          this.fetchTestimonials(this.currentPage);
          this.makeToast({ variant: 'success', msg: 'Testimonial deleted.' });
        }
      } catch (_err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },
  },

  async mounted() {
    this.fetchTestimonials();
  },
};
</script>

<style lang="scss" scoped>
.tt-quote {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  max-width: 420px;
  white-space: normal;
}
</style>
